import React, { useState, useEffect, useCallback, useRef } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { useApp } from "@/context/AppContext";
import { api } from "@/lib/api";
import Nav from "@/components/Nav";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { motion } from "framer-motion";
import {
  ArrowLeft, Loader2, RefreshCw, Clock, Database, AlertTriangle, MapPin, Star,
  BedDouble, TrendingUp, TrendingDown, ExternalLink, Activity, Camera, ThumbsUp, ThumbsDown, Sparkles, SlidersHorizontal, Coffee, CheckCircle2,
} from "lucide-react";

function PhotoInsightCard({ propId, cid, initial, lang, L }) {
  const [meta, setMeta] = useState(initial || null);
  const [loading, setLoading] = useState(false);
  const ins = meta?.insight || null;
  const canReanalyze = meta ? !!meta.can_reanalyze : true;
  const fmtD = (iso) => { try { const d = new Date(iso); return `${String(d.getDate()).padStart(2, "0")}/${String(d.getMonth() + 1).padStart(2, "0")}/${d.getFullYear()}`; } catch { return "—"; } };
  const run = async () => {
    if (ins && !canReanalyze) return;
    setLoading(true);
    try {
      const { data } = await api.post(`/properties/${propId}/competitor-photo-insight`, { competitor_id: cid, lang });
      setMeta({ insight: data.photo_insight, analyzed_at: data.analyzed_at, age_days: data.age_days,
                can_reanalyze: data.can_reanalyze, next_available_at: data.next_available_at,
                image_urls: data.image_urls || [] });
      toast.success(data.cached ? L("Analisi recuperata dal database", "Analysis loaded from database")
                                : L("Foto analizzate", "Photos analyzed"));
    } catch (e) { toast.error(e?.response?.data?.detail || L("Analisi non riuscita", "Analysis failed")); }
    finally { setLoading(false); }
  };
  const col = (v) => (v >= 66 ? "#16a34a" : v >= 40 ? "#b45309" : "#ef4444");
  const btnDisabled = loading || (!!ins && !canReanalyze);
  return (
    <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="bg-card border border-border rounded-lg p-6" data-testid="cd-photo-insight">
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <div className="flex items-center gap-2"><Camera className="w-5 h-5 text-primary" /><h3 className="text-sm uppercase tracking-widest font-bold text-muted-foreground">{L("Analisi foto (occhio del cliente)", "Photo analysis (guest's eye)")}</h3></div>
        <div className="flex items-center gap-2">
          {ins && !canReanalyze && meta?.next_available_at && (
            <span className="text-[11px] text-muted-foreground inline-flex items-center gap-1" data-testid="cd-photo-next">
              <Clock className="w-3.5 h-3.5" />{L(`Nuova analisi dal ${fmtD(meta.next_available_at)}`, `New analysis from ${fmtD(meta.next_available_at)}`)}
            </span>
          )}
          <Button data-testid="cd-photo-analyze-btn" size="sm" variant={ins ? "outline" : "default"} onClick={run} disabled={btnDisabled}
            title={ins && !canReanalyze ? L("Una nuova analisi è consentita una volta al mese", "A new analysis is allowed once a month") : undefined}>
            {loading ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Sparkles className="w-4 h-4 mr-1.5" />}
            {ins ? L("Rianalizza", "Re-analyze") : L("Analizza foto", "Analyze photos")}
          </Button>
        </div>
      </div>
      {!ins ? (
        <p className="text-sm text-muted-foreground">{L("Analizza le foto dell'annuncio Booking di questo competitor: l'AI le valuta come un cliente che deve prenotare e assegna un punteggio che influenza anche il Market Influence. L'analisi resta salvata e condivisa; una nuova è consentita una volta al mese.", "Analyze this competitor's Booking listing photos: the AI reviews them as a guest about to book and gives a score that also feeds the Market Influence. The analysis is saved and shared; a new one is allowed once a month.")}</p>
      ) : (
        <div className="space-y-4">
          <div className="flex items-center gap-4">
            <div className="text-center shrink-0">
              <div className="font-data text-3xl font-bold" style={{ color: col(ins.score) }} data-testid="cd-photo-score">{ins.score}<span className="text-sm text-muted-foreground font-normal">/100</span></div>
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Photo Score</div>
            </div>
            <div>
              {ins.headline && <p className="font-semibold text-sm">{ins.headline}</p>}
              {ins.perception && <p className="text-sm text-muted-foreground mt-0.5">{ins.perception}</p>}
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {ins.pros?.length > 0 && <div><p className="text-xs uppercase font-bold text-emerald-600 mb-1.5 inline-flex items-center gap-1"><ThumbsUp className="w-3.5 h-3.5" />{L("Punti di forza", "Pros")}</p><ul className="space-y-1">{ins.pros.map((p, i) => <li key={i} className="text-sm flex gap-2"><span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-emerald-500 shrink-0" />{p}</li>)}</ul></div>}
            {ins.cons?.length > 0 && <div><p className="text-xs uppercase font-bold text-amber-600 mb-1.5 inline-flex items-center gap-1"><ThumbsDown className="w-3.5 h-3.5" />{L("Punti deboli", "Cons")}</p><ul className="space-y-1">{ins.cons.map((p, i) => <li key={i} className="text-sm flex gap-2"><span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-amber-500 shrink-0" />{p}</li>)}</ul></div>}
          </div>
          {ins.verdict && <p className="text-sm border-t border-border pt-3"><b>{L("Verdetto", "Verdict")}:</b> {ins.verdict}</p>}
          {(meta?.image_urls?.length > 0) && (
            <div className="border-t border-border pt-3" data-testid="cd-photo-thumbs">
              <p className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground mb-2">{L("Foto analizzate dall'AI", "Photos analyzed by the AI")}</p>
              <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-2">
                {meta.image_urls.map((u, i) => (
                  <a key={i} href={u} target="_blank" rel="noreferrer" data-testid={`cd-photo-thumb-${i}`}
                    className="block aspect-square rounded-md overflow-hidden border border-border bg-muted hover:opacity-80 transition-opacity">
                    <img src={u} alt={`foto ${i + 1}`} loading="lazy" className="w-full h-full object-cover"
                      onError={(e) => { e.currentTarget.parentElement.style.display = "none"; }} />
                  </a>
                ))}
              </div>
            </div>
          )}
          <p className="text-[11px] text-muted-foreground">{L(`Foto analizzate: ${ins.photos_analyzed}${meta?.analyzed_at ? ` · analisi del ${fmtD(meta.analyzed_at)} (condivisa)` : ""} · questo punteggio contribuisce al Market Influence del competitor.`, `Photos analyzed: ${ins.photos_analyzed}${meta?.analyzed_at ? ` · analyzed ${fmtD(meta.analyzed_at)} (shared)` : ""} · this score contributes to the competitor's Market Influence.`)}</p>
        </div>
      )}
    </motion.div>
  );
}
import {
  ResponsiveContainer, ComposedChart, LineChart, BarChart, Bar, Area, Line, XAxis, YAxis,
  CartesianGrid, Tooltip, Legend,
} from "recharts";

const tooltipStyle = { background: "hsl(var(--popover))", border: "1px solid hsl(var(--border))", borderRadius: 8, fontSize: 12 };

const WEEKDAYS = {
  it: ["Lun", "Mar", "Mer", "Gio", "Ven", "Sab", "Dom"],
  en: ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"],
};
const DAY_TYPE = {
  weekday: { it: "Infrasettimanale", en: "Weekday", bg: "rgba(148,163,184,0.18)", fg: "#475569" },
  weekend: { it: "Weekend", en: "Weekend", bg: "rgba(59,130,246,0.16)", fg: "#2563eb" },
  holiday: { it: "Festivo", en: "Holiday", bg: "rgba(239,68,68,0.16)", fg: "#dc2626" },
};
const fullDate = (iso) => { try { const [y, m, d] = iso.split("-"); return `${d}/${m}/${y.slice(2)}`; } catch { return iso; } };

function Card({ children, className = "", testid }) {
  return (
    <motion.div data-testid={testid} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
      className={`bg-card border border-border rounded-lg p-6 ${className}`}>
      {children}
    </motion.div>
  );
}

const SIM_LABELS = {
  distance: ["Distanza", "Distance"], type: ["Tipologia", "Type"], rating: ["Rating", "Rating"],
  rooms: ["Camere", "Rooms"], amenities: ["Servizi", "Amenities"], price: ["Fascia prezzo", "Price band"],
  quality: ["Qualità", "Quality"],
};

const scoreColor = (v) => v == null ? "#64748b" : v >= 66 ? "#16a34a" : v >= 40 ? "#b45309" : "#94a3b8";

function ScoreChip({ label, value, highlight, badge }) {
  return (
    <div className={`rounded-lg p-4 ${highlight ? "border-2 border-primary/25 bg-primary/5" : "bg-muted/40"}`}>
      <p className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground">{label}</p>
      <p className="font-data text-2xl font-semibold mt-1" style={{ color: highlight ? undefined : scoreColor(value) }}>
        {value != null ? value : "—"}<span className="text-sm text-muted-foreground font-normal">/100</span>
      </p>
      {badge && <span className="inline-block mt-1 text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-amber-500/15 text-amber-600">{badge}</span>}
    </div>
  );
}

function CalibrationCard({ propId, cid, publicPrice, lang, L }) {
  const [pct, setPct] = useState("");
  const [note, setNote] = useState("");
  const [existing, setExisting] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    let on = true;
    (async () => {
      try {
        const { data } = await api.get(`/properties/${propId}/competitor-calibration`);
        const e = (data.calibration || []).find((x) => x.competitor_id === cid);
        if (on && e) { setExisting(e); setPct(String(e.pct)); setNote(e.note || ""); }
      } catch { /* noop */ }
    })();
    return () => { on = false; };
  }, [propId, cid]);
  const pctNum = parseFloat(pct);
  const engine = (publicPrice && !isNaN(pctNum)) ? Math.round(publicPrice * (1 + pctNum / 100)) : null;
  const fmtD = (iso) => { try { const d = new Date(iso); return `${String(d.getDate()).padStart(2, "0")}/${String(d.getMonth() + 1).padStart(2, "0")}/${d.getFullYear()}`; } catch { return "—"; } };
  const save = async () => {
    if (isNaN(pctNum)) { toast.error(L("Inserisci una percentuale", "Enter a percentage")); return; }
    setSaving(true);
    try {
      const { data } = await api.put(`/properties/${propId}/competitor-calibration`, { competitor_id: cid, pct: pctNum, note });
      setExisting(data.calibration);
      toast.success(L("Calibrazione salvata — il motore userà il prezzo stimato", "Calibration saved — the engine will use the estimated price"));
    } catch (e) { toast.error(e?.response?.data?.detail || L("Errore nel salvataggio", "Save failed")); }
    finally { setSaving(false); }
  };
  const clear = async () => {
    setSaving(true);
    try {
      await api.put(`/properties/${propId}/competitor-calibration`, { competitor_id: cid, pct: null });
      setExisting(null); setPct(""); setNote("");
      toast.success(L("Calibrazione rimossa", "Calibration cleared"));
    } catch { toast.error(L("Errore", "Error")); }
    finally { setSaving(false); }
  };
  return (
    <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="bg-card border border-border rounded-lg p-6" data-testid="cd-calibration">
      <div className="flex items-center gap-2 mb-2">
        <SlidersHorizontal className="w-5 h-5 text-primary" />
        <h3 className="text-sm uppercase tracking-widest font-bold text-muted-foreground">{L("Calibrazione competitor", "Competitor calibration")}</h3>
      </div>
      <p className="text-sm text-muted-foreground mb-4">{L("Il prezzo pubblico su Booking spesso non è quello reale (offerte, Genius, sconti). Inserisci la correzione osservata: il motore userà il prezzo stimato, mentre il prezzo pubblico resta invariato.", "The public Booking price is often not the real one (offers, Genius, discounts). Enter the observed correction: the engine will use the estimated price, while the public price stays unchanged.")}</p>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 items-end">
        <div>
          <label className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground">{L("Prezzo pubblico", "Public price")}</label>
          <p className="font-data text-2xl font-semibold mt-1" data-testid="cd-calibration-public">{publicPrice ? `€${Math.round(publicPrice)}` : "—"}</p>
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground">{L("Calibrazione %", "Calibration %")}</label>
          <input type="number" step="1" value={pct} onChange={(e) => setPct(e.target.value)} placeholder="-22"
            data-testid="cd-calibration-pct"
            className="mt-1 w-full h-10 rounded-md border border-border bg-background px-3 font-data text-lg focus:outline-none focus:ring-2 focus:ring-primary/40" />
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground">{L("Prezzo stimato (motore)", "Estimated price (engine)")}</label>
          <p className="font-data text-2xl font-semibold mt-1 text-primary" data-testid="cd-calibration-engine">{engine != null ? `€${engine}` : "—"}</p>
        </div>
      </div>
      <div className="mt-4">
        <label className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground">{L("Note (opzionale)", "Note (optional)")}</label>
        <input type="text" value={note} onChange={(e) => setNote(e.target.value)}
          placeholder={L("es. tariffa Genius -10% + offerta early booking", "e.g. Genius rate -10% + early booking offer")}
          data-testid="cd-calibration-note"
          className="mt-1 w-full h-10 rounded-md border border-border bg-background px-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary/40" />
      </div>
      <div className="flex items-center justify-between mt-4 flex-wrap gap-2">
        <span className="text-[11px] text-muted-foreground">
          {existing?.checked_at ? L(`Ultimo controllo: ${fmtD(existing.checked_at)}`, `Last check: ${fmtD(existing.checked_at)}`) : L("Nessuna calibrazione salvata", "No calibration saved")}
        </span>
        <div className="flex gap-2">
          {existing && <Button size="sm" variant="outline" onClick={clear} disabled={saving} data-testid="cd-calibration-clear">{L("Rimuovi", "Clear")}</Button>}
          <Button size="sm" onClick={save} disabled={saving} data-testid="cd-calibration-save">
            {saving ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : null}{L("Salva calibrazione", "Save calibration")}
          </Button>
        </div>
      </div>
    </motion.div>
  );
}

function BreakfastCard({ propId, cid, breakfast, publicPrice, lang, L, onSaved }) {
  const [price, setPrice] = useState(breakfast?.breakfast_price != null ? String(breakfast.breakfast_price) : "");
  const [includes, setIncludes] = useState(breakfast?.includes_override === true);
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    setPrice(breakfast?.breakfast_price != null ? String(breakfast.breakfast_price) : "");
    setIncludes(breakfast?.includes_override === true);
  }, [breakfast?.breakfast_price, breakfast?.includes_override, cid]);
  const fmtD = (iso) => { try { const d = new Date(iso); return `${String(d.getDate()).padStart(2, "0")}/${String(d.getMonth() + 1).padStart(2, "0")}/${d.getFullYear()} ${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`; } catch { return "—"; } }
  const bfNum = parseFloat(price);
  // Effective inclusion: manual toggle wins; else the scraped flag.
  const inclEff = includes || breakfast?.scraped_breakfast_included === true;
  const roPrice = (publicPrice && inclEff && !isNaN(bfNum)) ? Math.max(1, Math.round(publicPrice - bfNum)) : null;
  const save = async () => {
    if (isNaN(bfNum) || bfNum < 0) { toast.error(L("Inserisci il prezzo della colazione", "Enter the breakfast price")); return; }
    setSaving(true);
    try {
      await api.put(`/properties/${propId}/competitor-breakfast`, { competitor_id: cid, breakfast_price: bfNum, includes_override: includes ? true : null });
      toast.success(L("Prezzo colazione salvato — ricalcolo del consiglio aggiornato", "Breakfast price saved — recommendation recalculated"));
      onSaved && onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || L("Errore nel salvataggio", "Save failed")); }
    finally { setSaving(false); }
  };
  const clear = async () => {
    setSaving(true);
    try {
      await api.put(`/properties/${propId}/competitor-breakfast`, { competitor_id: cid, breakfast_price: null, includes_override: null, price_estimated: false });
      setPrice("");
      setIncludes(false);
      toast.success(L("Impostazione colazione rimossa", "Breakfast setting cleared"));
      onSaved && onSaved();
    } catch { toast.error(L("Errore", "Error")); }
    finally { setSaving(false); }
  };
  const markRoomOnly = async () => {
    setSaving(true);
    try {
      await api.put(`/properties/${propId}/competitor-breakfast`, { competitor_id: cid, includes_override: false, breakfast_price: null });
      toast.success(L("Segnato: il prezzo rilevato è senza colazione", "Marked: detected price is room only"));
      onSaved && onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || L("Errore", "Error")); }
    finally { setSaving(false); }
  };
  const markInclusiveNoPrice = async () => {
    setSaving(true);
    try {
      await api.put(`/properties/${propId}/competitor-breakfast`, { competitor_id: cid, no_price_available: true });
      toast.success(L("Segnato: l'host usa solo tariffe con colazione (costo stimato)", "Marked: host only sells breakfast-inclusive rates (estimated cost)"));
      onSaved && onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || L("Errore", "Error")); }
    finally { setSaving(false); }
  };
  const Badge = () => {
    if (breakfast?.needs_breakfast_price) {
      return (
        <span data-testid="cd-bf-badge-missing" className="inline-flex items-center gap-1 text-[11px] font-bold px-2.5 py-1 rounded-full bg-red-500/15 text-red-600 dark:text-red-400 animate-pulse">
          <AlertTriangle className="w-3.5 h-3.5" />{breakfast?.label === "included" ? L("Prezzo CON colazione — inserisci il costo colazione", "Price WITH breakfast — enter breakfast cost") : L("Colazione da verificare — inserisci il costo", "Breakfast to verify — enter the cost")}
        </span>
      );
    }
    if (breakfast?.verified) {
      return (
        <span data-testid="cd-bf-badge-verified" className="inline-flex items-center gap-1 text-[11px] font-bold px-2.5 py-1 rounded-full bg-emerald-500/15 text-emerald-600 dark:text-emerald-400">
          <CheckCircle2 className="w-3.5 h-3.5" />{L("Verificato", "Verified")}{breakfast?.breakfast_updated_at ? ` · ${fmtD(breakfast.breakfast_updated_at)}` : ""}
        </span>
      );
    }
    return (
      <span className="inline-flex items-center gap-1 text-[11px] font-bold px-2.5 py-1 rounded-full bg-muted text-muted-foreground">
        <Coffee className="w-3.5 h-3.5" />{L("Senza colazione", "Room Only")}
      </span>
    );
  };
  return (
    <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="bg-card border border-border rounded-lg p-6" data-testid="cd-breakfast">
      <div className="flex items-center gap-2 mb-2 flex-wrap">
        <Coffee className="w-5 h-5 text-primary" />
        <h3 className="text-sm uppercase tracking-widest font-bold text-muted-foreground">{L("Colazione del competitor", "Competitor breakfast")}</h3>
        <Badge />
      </div>
      <p className="text-sm text-muted-foreground mb-4">{L("Il prezzo Booking del competitor può includere o meno la colazione. Verifica a mano sul suo Booking la differenza tra tariffa con e senza colazione e inserisci qui il prezzo della sola colazione: quando il prezzo rilevato è CON colazione, il motore lo scorpora e confronta Room Only vs Room Only.", "The competitor's Booking price may or may not include breakfast. Check manually on their Booking the gap between the rate with and without breakfast and enter the breakfast-only price here: when the detected price is WITH breakfast, the engine strips it and compares Room Only vs Room Only.")}</p>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 items-end">
        <div>
          <label className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground">{L("Prezzo rilevato", "Detected price")}</label>
          <p className="font-data text-2xl font-semibold mt-1">{publicPrice ? `€${Math.round(publicPrice)}` : "—"}</p>
          <p className="text-[11px] text-muted-foreground mt-0.5">{inclEff ? L("con colazione", "with breakfast") : breakfast?.label === "excluded" ? L("senza colazione", "room only") : L("colazione da verificare", "breakfast to verify")}</p>
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground">{L("Prezzo colazione (€)", "Breakfast price (€)")}</label>
          <input type="number" step="0.5" value={price} onChange={(e) => setPrice(e.target.value)} placeholder="8"
            data-testid="cd-bf-price"
            className="mt-1 w-full h-10 rounded-md border border-border bg-background px-3 font-data text-lg focus:outline-none focus:ring-2 focus:ring-primary/40" />
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground">{L("Prezzo Room Only (motore)", "Room Only price (engine)")}</label>
          <p className="font-data text-2xl font-semibold mt-1 text-primary" data-testid="cd-bf-ro">{roPrice != null ? `€${roPrice}` : (!inclEff && breakfast?.label === "excluded" && publicPrice ? `€${Math.round(publicPrice)}` : "—")}</p>
        </div>
      </div>
      <label className="flex items-center gap-2.5 mt-4 cursor-pointer select-none" data-testid="cd-bf-toggle-label">
        <input type="checkbox" checked={includes} onChange={(e) => setIncludes(e.target.checked)}
          data-testid="cd-bf-includes" className="w-4 h-4 accent-[var(--primary)] cursor-pointer" />
        <span className="text-sm">{L("Ho verificato: il prezzo rilevato INCLUDE la colazione", "I verified: the detected price INCLUDES breakfast")}
          <span className="block text-[11px] text-muted-foreground">{L("Attivalo per scorporare subito la colazione senza attendere la prossima scansione.", "Turn on to strip breakfast immediately without waiting for the next scan.")}</span>
        </span>
      </label>
      <div className="mt-4 pt-4 border-t border-border">
        <p className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground mb-2">{L("Non conosci il prezzo esatto?", "Don't know the exact price?")}</p>
        <div className="flex flex-wrap gap-2">
          <Button size="sm" variant="outline" onClick={markRoomOnly} disabled={saving} data-testid="cd-bf-room-only">
            {L("Il prezzo rilevato NON include la colazione", "Detected price does NOT include breakfast")}
          </Button>
          <Button size="sm" variant="outline" onClick={markInclusiveNoPrice} disabled={saving} data-testid="cd-bf-inclusive-noprice">
            {L("L'host usa solo tariffe con colazione (costo non specificato)", "Host only sells breakfast-inclusive rates (cost not specified)")}
          </Button>
        </div>
        {breakfast?.price_estimated && (
          <p className="text-[11px] text-amber-600 dark:text-amber-400 mt-2" data-testid="cd-bf-estimated-note">
            {L("Costo colazione stimato automaticamente (l'host non specifica il prezzo). Puoi comunque inserire un valore preciso qui sopra.", "Breakfast cost estimated automatically (host doesn't specify a price). You can still enter a precise value above.")}
          </p>
        )}
      </div>
      <div className="flex items-center justify-end mt-4 gap-2">
        {breakfast?.verified && <Button size="sm" variant="outline" onClick={clear} disabled={saving} data-testid="cd-bf-clear">{L("Rimuovi", "Clear")}</Button>}
        <Button size="sm" onClick={save} disabled={saving} data-testid="cd-bf-save">
          {saving ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : null}{L("Salva prezzo colazione", "Save breakfast price")}
        </Button>
      </div>
    </motion.div>
  );
}



export default function CompetitorDetail() {
  const { lang } = useApp();
  const L = (it, en) => (lang === "en" ? en : it);
  const [params] = useSearchParams();
  const nav = useNavigate();
  const propId = params.get("prop");
  const cid = params.get("cid");
  const stayDate = params.get("stay") || "";

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [scanning, setScanning] = useState(false);
  const [scanProgress, setScanProgress] = useState(null);
  const pollRef = useRef(null);
  const autoRef = useRef(false);

  const load = useCallback(async () => {
    if (!propId || !cid) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/properties/${propId}/competitor-detail`, {
        params: { competitor_id: cid, stay_date: stayDate, lang },
      });
      setData(data);
      return data;
    } catch { setData(null); return null; }
    finally { setLoading(false); }
  }, [propId, cid, stayDate, lang]);

  const [savingMin2, setSavingMin2] = useState(false);
  const toggleMin2 = async () => {
    if (!data) return;
    setSavingMin2(true);
    try {
      const enabled = !data.min2_nights;
      await api.post(`/properties/${propId}/competitors/${encodeURIComponent(cid)}/min2-nights`, { enabled });
      setData((d) => ({ ...d, min2_nights: enabled }));
      toast.success(enabled
        ? L("Impostato: la prossima scansione userà soggiorni da 2 notti per questo competitor", "Set: next scan will use 2-night stays for this competitor")
        : L("Ripristinato: scansione a 1 notte", "Reset: 1-night scan"));
    } catch (e) { toast.error(L("Errore nel salvataggio", "Save error")); }
    finally { setSavingMin2(false); }
  };

  const pollScan = useCallback(() => {
    if (pollRef.current) return;
    setScanning(true);
    pollRef.current = setInterval(async () => {
      try {
        const { data: st } = await api.get(`/properties/${propId}/competitors/scan-status`);
        if (st.scan?.status === "running") {
          setScanProgress({ scanned: st.scan.scanned || 0, total: st.scan.total || 0, recorded: st.scan.recorded || 0 });
        }
        if (st.scan?.status !== "running") {
          clearInterval(pollRef.current); pollRef.current = null; setScanning(false); setScanProgress(null);
          if (st.scan?.status === "error") toast.error((st.scan?.error ? L(`Scansione fallita: ${st.scan.error}`, `Scan failed: ${st.scan.error}`) : L("Scansione fallita (token o crediti Apify?).", "Scan failed (Apify token or credits?).")), { duration: 8000 });
          else if (st.scan?.status === "done") toast.success(L(`Prezzi aggiornati (${st.scan.recorded ?? st.scan.scanned} date con dati).`, `Prices updated (${st.scan.recorded ?? st.scan.scanned} dates with data).`));
          load();
        }
      } catch { clearInterval(pollRef.current); pollRef.current = null; setScanning(false); setScanProgress(null); load(); }
    }, 4000);
  }, [propId, lang, load]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => () => { if (pollRef.current) clearInterval(pollRef.current); }, []);

  // First time only: auto-trigger ONE Apify price scan for the selected competitors.
  useEffect(() => {
    if (!data || autoRef.current) return;
    if (data.scan_state?.status === "running") { autoRef.current = true; pollScan(); return; }
    if (data.price_scan && data.price_scan.ever_scanned === false) {
      autoRef.current = true;
      (async () => {
        try {
          const { data: res } = await api.post(`/properties/${propId}/competitors/scan-prices`, {});
          if (res.status === "running") { toast.info(L("Prima scansione prezzi avviata…", "First price scan started…")); pollScan(); }
        } catch { /* silent */ }
      })();
    }
  }, [data, propId, pollScan, lang]);

  // Manual "Aggiorna prezzi": scan the smart grid. Respects the shared 7-day cache.
  const scanPrices = async (force = false) => {
    if (scanning) return;
    setScanning(true);
    try {
      const { data: res } = await api.post(`/properties/${propId}/competitors/scan-prices`, { force });
      if (res.status === "running") { toast.info(L("Aggiornamento prezzi avviato…", "Price update started…")); pollScan(); }
      else if (res.status === "cached") {
        setScanning(false);
        const ok = window.confirm(L(
          `${res.message} Riscansionare comunque? Consumerà crediti Apify.`,
          `${res.message} Rescan anyway? This will consume Apify credits.`));
        if (ok) scanPrices(true);
      } else if (res.status === "no_competitors") {
        setScanning(false); toast.error(L("Nessun competitor selezionato da analizzare.", "No selected competitor to analyze."));
      } else { setScanning(false); load(); }
    } catch (e) { setScanning(false); toast.error(e?.response?.data?.detail || L("Errore scansione", "Scan error")); }
  };

  const fmtDT = (iso) => { try { const dt = new Date(iso); return `${String(dt.getDate()).padStart(2, "0")}/${String(dt.getMonth() + 1).padStart(2, "0")} ${String(dt.getHours()).padStart(2, "0")}:${String(dt.getMinutes()).padStart(2, "0")}`; } catch { return "—"; } };
  const fmtDate = (iso) => { try { const [y, m, d] = iso.split("-"); return `${d}/${m}`; } catch { return iso; } };

  const reg = data?.registry || {};
  const forward = (data?.forward_curve || []).map((c) => ({
    date: fmtDate(c.stay_date), price: c.price, rooms: c.rooms_available,
    occ: c.occupancy_est, soldOut: c.sold_out,
  }));
  const pickup = (data?.pickup || []).map((p) => ({
    day: p.day ? p.day.slice(5) : "—", price: p.price, rooms: p.rooms_available, occ: p.occupancy_est,
  }));
  const monthly = (data?.monthly_trend || []).map((m) => ({
    month: m.month, avgPrice: m.avg_price, avgOcc: m.avg_occupancy, samples: m.samples,
  }));
  const v = data?.variations || {};

  const Stat = ({ label, value, sub, delta }) => (
    <div className="bg-muted/40 rounded-lg p-4">
      <p className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground">{label}</p>
      <p className="font-data text-2xl font-semibold mt-1">{value}
        {delta != null && delta !== 0 && (
          <span className="ml-2 inline-flex items-center text-xs font-medium" style={{ color: delta > 0 ? "#ef4444" : "#16a34a" }}>
            {delta > 0 ? <TrendingUp className="w-3.5 h-3.5" /> : <TrendingDown className="w-3.5 h-3.5" />}{delta > 0 ? "+" : ""}{delta}
          </span>
        )}
      </p>
      {sub && <p className="text-xs text-muted-foreground mt-0.5">{sub}</p>}
    </div>
  );

  const EmptyChart = ({ label }) => (
    <div className="h-[200px] flex flex-col items-center justify-center text-center text-muted-foreground">
      <Activity className="w-6 h-6 mb-2 opacity-40" />
      <p className="text-xs">{label}</p>
    </div>
  );

  return (
    <div className="App min-h-screen"><Nav />
      <div className="max-w-6xl mx-auto px-6 py-8" data-testid="competitor-detail-page">
        <div className="flex items-center justify-between gap-3 mb-6 flex-wrap">
          <Button data-testid="cd-back" variant="ghost" size="sm" onClick={() => nav("/dashboard")}>
            <ArrowLeft className="w-4 h-4 mr-1.5" />{L("Torna alla dashboard", "Back to dashboard")}
          </Button>
          <div className="flex items-center gap-2 flex-wrap">
            {data?.price_scan?.last_scan_at && (
              <span className="inline-flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-full bg-muted text-muted-foreground" data-testid="cd-last-updated">
                <Clock className="w-3.5 h-3.5" />{L("Prezzi rilevati", "Prices scanned")} {fmtDT(data.price_scan.last_scan_at)} · {data.price_scan.age_days}g
              </span>
            )}
            {data?.price_scan && data.price_scan.needs_scan && (
              <span className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full" style={{ backgroundColor: "rgba(245,158,11,0.16)", color: "#b45309" }} data-testid="cd-needs-refresh">
                <AlertTriangle className="w-3.5 h-3.5" />{L("Prezzi da aggiornare", "Prices need update")}
              </span>
            )}
            <Button data-testid="cd-refresh" size="sm" onClick={() => scanPrices(false)} disabled={scanning}>
              {scanning ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-1.5" />}
              {scanning
                ? (scanProgress && scanProgress.total
                    ? L(`Aggiorno… ${scanProgress.scanned}/${scanProgress.total}`, `Updating… ${scanProgress.scanned}/${scanProgress.total}`)
                    : L("Aggiorno…", "Updating…"))
                : L("Aggiorna prezzi", "Update prices")}
            </Button>
            {scanning && (
              <span className="text-[11px] text-muted-foreground w-full mt-1" data-testid="cd-scan-hint">
                {L("La scansione dei 6 mesi può richiedere alcuni minuti: puoi lasciare la pagina aperta.", "The 6-month scan can take a few minutes: you can leave this page open.")}
              </span>
            )}
          </div>
        </div>

        {loading ? (
          <div className="py-24 text-center text-muted-foreground"><Loader2 className="w-6 h-6 animate-spin inline mr-2" />{L("Carico i dati storici…", "Loading historical data…")}</div>
        ) : !data ? (
          <div className="py-24 text-center text-muted-foreground">{L("Competitor non trovato.", "Competitor not found.")}</div>
        ) : (
          <div className="space-y-6">
            {/* Header / identity */}
            <Card testid="cd-identity">
              <div className="flex items-start justify-between gap-4 flex-wrap">
                <div>
                  <h1 className="text-3xl font-bold tracking-tight">{reg.name || L("Competitor", "Competitor")}</h1>
                  <div className="flex items-center gap-3 mt-2 text-sm text-muted-foreground flex-wrap">
                    {reg.category && <span className="inline-flex items-center gap-1"><MapPin className="w-4 h-4" />{reg.category}</span>}
                    {reg.distance_km_ref != null && <span>{reg.distance_km_ref} km</span>}
                    {reg.rating && <span className="inline-flex items-center gap-1"><Star className="w-4 h-4 fill-current text-amber-500" />{reg.rating}</span>}
                    {reg.total_rooms && <span className="inline-flex items-center gap-1"><BedDouble className="w-4 h-4" />{reg.total_rooms} {L("camere", "rooms")}{reg.total_rooms_source ? ` · ${reg.total_rooms_source}` : ""}</span>}
                  </div>
                </div>
                {reg.url && (
                  <a href={reg.url} target="_blank" rel="noreferrer" data-testid="cd-booking-link"
                    className="inline-flex items-center gap-1.5 text-sm text-primary hover:underline">
                    <ExternalLink className="w-4 h-4" />{L("Vedi su Booking", "View on Booking")}
                  </a>
                )}
              </div>
              {(() => {
                const inf = data.influence || {};
                const sim = inf.similarity_score ?? inf.similarity;
                const bd = inf.similarity_breakdown || {};
                const PHR = {
                  type: ["stessa tipologia di struttura", "same property type"],
                  price: ["fascia di prezzo simile alla tua", "similar price band to yours"],
                  rating: ["punteggio recensioni vicino al tuo", "review score close to yours"],
                  rooms: ["dimensioni (numero camere) comparabili", "comparable size (room count)"],
                  amenities: ["servizi e dotazioni simili", "similar amenities"],
                  quality: ["livello di qualità simile", "similar quality level"],
                };
                const reasons = Object.entries(bd)
                  .filter(([k, v]) => k !== "distance" && v >= 55 && PHR[k])
                  .sort((a, b) => b[1] - a[1])
                  .slice(0, 3)
                  .map(([k]) => L(PHR[k][0], PHR[k][1]));
                if (reg.distance_km_ref != null)
                  reasons.unshift(L(`a soli ${reg.distance_km_ref} km da te`, `just ${reg.distance_km_ref} km away`));
                const isPrice = inf.comp_type === "price";
                return (
                  <div className="mt-4 rounded-lg border border-primary/20 bg-primary/5 p-4" data-testid="cd-selection-reason">
                    <p className="text-[10px] uppercase tracking-widest font-bold text-primary mb-1.5">
                      {L("Perché è un tuo competitor diretto", "Why it's a direct competitor of yours")}
                    </p>
                    <p className="text-sm text-foreground/90 leading-relaxed">
                      {sim != null
                        ? L(`Revenue AI l'ha scelto perché è tra le strutture più simili alla tua (indice di similarità ${sim}/100)`,
                             `Revenue AI picked it because it's among the most similar properties to yours (similarity index ${sim}/100)`)
                        : L("Revenue AI l'ha scelto per vicinanza e affinità con la tua struttura", "Revenue AI picked it for proximity and affinity with your property")}
                      {reasons.length > 0 && <>: {reasons.join(L(", ", ", "))}</>}
                      {reg.category ? L(`. Categoria: ${String(reg.category).replace(/_/g, " ")}.`, `. Category: ${String(reg.category).replace(/_/g, " ")}.`) : "."}
                      {" "}
                      {isPrice
                        ? L("Per questo entra nel calcolo diretto del tuo prezzo consigliato.", "That's why it feeds directly into your recommended price.")
                        : L("Serve come riferimento di mercato per la tua strategia.", "It serves as a market reference for your strategy.")}
                    </p>
                  </div>
                );
              })()}
              {data.snapshots_total <= 3 && (
                <p className="mt-4 text-xs px-3 py-2 rounded-md bg-amber-500/10 text-amber-700 dark:text-amber-400" data-testid="cd-accumulating">
                  {L(`Dati in accumulo (${data.snapshots_total} rilevazioni). I grafici si arricchiranno con le scansioni automatiche pianificate.`, `Data accumulating (${data.snapshots_total} snapshots). Charts will fill in with the scheduled automatic scans.`)}
                </p>
              )}
            </Card>

            {/* Competitor Influence Engine */}
            {data.influence && (
              <Card testid="cd-influence">
                <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
                  <h3 className="text-sm uppercase tracking-widest font-bold text-muted-foreground">{L("Motore di influenza", "Influence engine")}</h3>
                  {data.influence.comp_type && (
                    <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold" data-testid="cd-comp-type"
                      style={data.influence.comp_type === "price"
                        ? { background: "hsl(var(--primary)/0.12)", color: "hsl(var(--primary))" }
                        : { background: "rgba(100,116,139,0.14)", color: "#64748b" }}>
                      {data.influence.comp_type === "price"
                        ? L("🎯 Competitor di prezzo", "🎯 Price competitor")
                        : L("🎯 Competitor di mercato", "🎯 Market competitor")}
                    </span>
                  )}
                </div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                  <ScoreChip label={L("Similarità", "Similarity")} value={data.influence.similarity_score ?? data.influence.similarity} />
                  <ScoreChip label={L("Market Influence", "Market Influence")} value={data.influence.market_influence_score} highlight />
                  <ScoreChip label={L("Affidabilità dati", "Data reliability")} value={data.influence.reliability_score} />
                  <ScoreChip label={L("Pickup Leader", "Pickup Leader")} value={data.influence.pickup_leader_score}
                    badge={data.influence.pickup_role === "leader" ? L("Leader", "Leader") : data.influence.pickup_role === "follower" ? L("Follower", "Follower") : null} />
                </div>
                <div className="flex flex-wrap items-center gap-x-6 gap-y-2 text-xs text-muted-foreground">
                  <span>{L("Peso nel pricing", "Pricing weight")}: <b className="text-foreground font-data">{data.influence.pricing_weight != null ? `${Math.round(data.influence.pricing_weight * 100)}%` : "—"}</b></span>
                  <span>{L("Trend prezzo", "Price trend")}: <b className="text-foreground font-data">{data.influence.price_trend_score ?? "—"}</b></span>
                  <span>{L("Frequenza", "Frequency")}: <b className="text-foreground font-data">{data.influence.frequency_pct != null ? `${data.influence.frequency_pct}%` : "—"}</b></span>
                  <span>{L("Rilevanza taglia", "Size relevance")}: <b className="text-foreground font-data">{data.influence.capacity_weight != null ? `${Math.round(data.influence.capacity_weight * 100)}%` : "—"}</b></span>
                  {data.influence.segment && <span>{L("Segmento", "Segment")}: <b className="text-foreground">{data.influence.segment}</b></span>}
                </div>
                {data.influence.similarity_breakdown && (
                  <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="cd-sim-breakdown">
                    {Object.entries(data.influence.similarity_breakdown).map(([k, val]) => (
                      <div key={k}>
                        <div className="flex justify-between text-[10px] uppercase tracking-wide text-muted-foreground mb-1">
                          <span>{SIM_LABELS[k]?.[lang === "en" ? 1 : 0] || k}</span><span className="font-data">{val}%</span>
                        </div>
                        <div className="h-1.5 rounded-full bg-muted overflow-hidden"><div className="h-full rounded-full bg-primary" style={{ width: `${val}%` }} /></div>
                      </div>
                    ))}
                  </div>
                )}
                <p className="text-[11px] text-muted-foreground mt-4">{L("Questi punteggi si ricalcolano automaticamente ogni notte con i nuovi dati raccolti: il competitor 'vive' e il suo peso nel prezzo evolve nel tempo.", "These scores are recomputed automatically every night with new data: the competitor is 'alive' and its weight in pricing evolves over time.")}</p>
              </Card>
            )}

            {/* Photo Insight — sempre disponibile nel dettaglio competitor */}
            <PhotoInsightCard propId={propId} cid={cid} initial={data.photo} lang={lang} L={L} />

            {/* Competitor Calibration — correzione manuale del prezzo pubblico */}
            <CalibrationCard propId={propId} cid={cid} publicPrice={(data.price_list && data.price_list[0] && data.price_list[0].price) || data.variations?.price_last} lang={lang} L={L} />

            {/* Competitor Breakfast — normalizzazione Room Only (solo competitor selezionati) */}
            {data.breakfast?.is_monitored && (
              <BreakfastCard propId={propId} cid={cid} breakfast={data.breakfast}
                publicPrice={(data.price_list && data.price_list[0] && data.price_list[0].price) || data.variations?.price_last}
                lang={lang} L={L} onSaved={load} />
            )}

            {/* Variations summary */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="cd-variations">
              <Stat label={L("Prezzo attuale", "Current price")} value={v.price_last != null ? `€${Math.round(v.price_last)}` : "—"} delta={v.price_delta} sub={L("Market Pickup", "Market Pickup")} />
              <Stat label={L("Disponibilità", "Availability")} value={v.availability_last != null ? v.availability_last : "—"} delta={v.availability_delta} sub={L("camere residue", "rooms left")} />
              <Stat label={L("Occupazione stim.", "Est. occupancy")} value={forward.length ? `${forward[forward.length - 1].occ ?? "—"}%` : "—"} />
              <Stat label={L("Rilevazioni", "Snapshots")} value={data.snapshots_total} sub={L("storico salvato", "saved history")} />
            </div>

            {/* Analyzed prices list (with weekday + day type) */}
            {(data.price_list || []).length > 0 && (
              <Card testid="cd-price-list">
                <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
                  <h3 className="text-sm uppercase tracking-widest font-bold text-muted-foreground">{L("Prezzi analizzati", "Analyzed prices")}</h3>
                  <div className="flex items-center gap-3 flex-wrap">
                    {data.breakfast?.is_monitored && (
                      <button data-testid="cd-min2-toggle" onClick={toggleMin2} disabled={savingMin2}
                        title={L("Se questo competitor ha spesso un minimo di 2 notti, attiva questa opzione: la prossima scansione 6 mesi lo cercherà con soggiorni da 2 notti (così Booking restituisce un prezzo invece di 'n.d.').", "If this competitor often has a 2-night minimum, enable this: the next 6-month scan will query it with 2-night stays (so Booking returns a price instead of 'n.d.').")}
                        className={`inline-flex items-center gap-1.5 text-[11px] font-medium px-2.5 py-1 rounded-full border transition-colors ${data.min2_nights ? "bg-sky-500/15 text-sky-600 border-sky-500/40" : "border-border text-muted-foreground hover:bg-accent"}`}>
                        <span className={`w-2 h-2 rounded-full ${data.min2_nights ? "bg-sky-500" : "bg-muted-foreground/40"}`} />
                        {L("Possibile minimo 2 notti", "Possible 2-night minimum")}{data.min2_nights ? L(" · attivo", " · on") : ""}
                      </button>
                    )}
                    <span className="text-[11px] text-muted-foreground">
                      {data.price_list.length} {L("date analizzate", "dates analyzed")}
                      {" · "}
                      {data.price_list.filter((p) => p.price != null).length} {L("con prezzo", "with price")}
                      {" · "}{L("griglia intelligente 6 mesi", "6-month smart grid")}
                    </span>
                  </div>
                </div>
                <div className="overflow-x-auto max-h-[340px] overflow-y-auto">
                  <table className="w-full text-sm">
                    <thead className="sticky top-0 bg-card">
                      <tr className="text-[10px] uppercase tracking-wide text-muted-foreground border-b border-border">
                        <th className="text-left px-2 py-2">{L("Data", "Date")}</th>
                        <th className="text-left px-2 py-2">{L("Giorno", "Day")}</th>
                        <th className="text-left px-2 py-2">{L("Tipo", "Type")}</th>
                        <th className="text-right px-2 py-2">{L("Prezzo", "Price")}</th>
                        <th className="text-right px-2 py-2">{L("Camere", "Rooms")}</th>
                        <th className="text-right px-2 py-2">{L("Occ.", "Occ.")}</th>
                      </tr>
                    </thead>
                    <tbody data-testid="cd-price-rows">
                      {data.price_list.map((p, i) => {
                        const dt = DAY_TYPE[p.day_type] || {};
                        return (
                          <tr key={i} data-testid={`cd-price-row-${i}`} className={`border-b border-border/40 hover:bg-accent/20 ${p.no_data ? "opacity-60" : ""}`}>
                            <td className="px-2 py-1.5 font-data">{fullDate(p.stay_date)}</td>
                            <td className="px-2 py-1.5">{WEEKDAYS[lang === "en" ? "en" : "it"][p.weekday] ?? "—"}</td>
                            <td className="px-2 py-1.5">
                              <span className="text-[10px] px-1.5 py-0.5 rounded-full" style={{ backgroundColor: dt.bg, color: dt.fg }}>{L(dt.it, dt.en) || "—"}</span>
                            </td>
                            <td className="px-2 py-1.5 text-right font-data font-semibold">
                              {p.no_data
                                ? <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-muted text-muted-foreground" title={L("Non restituito da Booking per questa data: probabile minimo soggiorno o non disponibile per 1 notte.", "Not returned by Booking for this date: likely minimum stay or unavailable for a 1-night search.")}>{L("n.d.", "n/a")}</span>
                                : p.sold_out
                                  ? <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-red-500/15 text-red-600">SOLD OUT</span>
                                  : (p.price != null
                                      ? <span className="inline-flex items-center gap-1 justify-end flex-wrap">
                                          €{Math.round(p.price)}
                                          {p.nights > 1 && (
                                            <span className="text-[9px] font-normal px-1 py-0.5 rounded bg-sky-500/15 text-sky-600" title={L(`Prezzo per soggiorno minimo di ${p.nights} notti. Totale ${p.nights} notti: €${p.price_total ?? Math.round(p.price * p.nights)} (diviso per darti il prezzo a notte).`, `Price for a ${p.nights}-night minimum stay. ${p.nights}-night total: €${p.price_total ?? Math.round(p.price * p.nights)} (divided to give the per-night price).`)}>
                                              {L(`min ${p.nights} notti`, `min ${p.nights} nights`)} · {L("tot", "tot")} €{p.price_total ?? Math.round(p.price * p.nights)}
                                            </span>
                                          )}
                                        </span>
                                      : "—")}
                            </td>
                            <td className="px-2 py-1.5 text-right font-data">{p.rooms_available != null ? p.rooms_available : "—"}</td>
                            <td className="px-2 py-1.5 text-right font-data">{p.occupancy_est != null ? `${p.occupancy_est}%` : "—"}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </Card>
            )}

            {/* Forward price + availability */}
            <Card testid="cd-forward-chart">
              <h3 className="text-sm uppercase tracking-widest font-bold text-muted-foreground mb-4">{L("Prezzo e disponibilità per data di soggiorno", "Price & availability by stay date")}</h3>
              {forward.length >= 2 ? (
                <ResponsiveContainer width="100%" height={220}>
                  <ComposedChart data={forward}>
                    <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                    <XAxis dataKey="date" tick={{ fontSize: 10 }} stroke="hsl(var(--muted-foreground))" />
                    <YAxis yAxisId="l" tick={{ fontSize: 10 }} stroke="hsl(var(--muted-foreground))" />
                    <YAxis yAxisId="r" orientation="right" tick={{ fontSize: 10 }} stroke="hsl(var(--muted-foreground))" />
                    <Tooltip contentStyle={tooltipStyle} />
                    <Legend wrapperStyle={{ fontSize: 11 }} />
                    <Area yAxisId="r" name={L("Camere", "Rooms")} type="monotone" dataKey="rooms" stroke="hsl(var(--chart-2))" fill="hsl(var(--chart-2))" fillOpacity={0.15} />
                    <Line yAxisId="l" name={L("Prezzo €", "Price €")} type="monotone" dataKey="price" stroke="hsl(var(--primary))" strokeWidth={2} dot={{ r: 2 }} connectNulls />
                  </ComposedChart>
                </ResponsiveContainer>
              ) : <EmptyChart label={L("Servono più date di soggiorno per il grafico. In accumulo.", "More stay dates needed for this chart. Accumulating.")} />}
            </Card>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Occupancy forward */}
              <Card testid="cd-occupancy-chart">
                <h3 className="text-sm uppercase tracking-widest font-bold text-muted-foreground mb-4">{L("Occupazione stimata per data", "Estimated occupancy by date")}</h3>
                {forward.length >= 2 ? (
                  <ResponsiveContainer width="100%" height={200}>
                    <LineChart data={forward}>
                      <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                      <XAxis dataKey="date" tick={{ fontSize: 10 }} stroke="hsl(var(--muted-foreground))" />
                      <YAxis domain={[0, 100]} tick={{ fontSize: 10 }} stroke="hsl(var(--muted-foreground))" />
                      <Tooltip contentStyle={tooltipStyle} />
                      <Line name={L("Occ. %", "Occ. %")} type="monotone" dataKey="occ" stroke="hsl(var(--chart-4))" strokeWidth={2} dot={{ r: 2 }} connectNulls />
                    </LineChart>
                  </ResponsiveContainer>
                ) : <EmptyChart label={L("Dati occupazione in accumulo.", "Occupancy data accumulating.")} />}
              </Card>

              {/* Market Pickup for selected stay date */}
              <Card testid="cd-pickup-chart">
                <h3 className="text-sm uppercase tracking-widest font-bold text-muted-foreground mb-1">{L("Market Pickup", "Market Pickup")}</h3>
                <p className="text-xs text-muted-foreground mb-3">{stayDate ? L(`Andamento prezzo/camere avvicinandosi al ${fmtDate(stayDate)}`, `Price/rooms trend approaching ${fmtDate(stayDate)}`) : L("Seleziona una data soggiorno nel Consiglio Prezzo", "Select a stay date in the Price Advisor")}</p>
                {pickup.length >= 2 ? (
                  <ResponsiveContainer width="100%" height={200}>
                    <ComposedChart data={pickup}>
                      <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                      <XAxis dataKey="day" tick={{ fontSize: 10 }} stroke="hsl(var(--muted-foreground))" />
                      <YAxis yAxisId="l" tick={{ fontSize: 10 }} stroke="hsl(var(--muted-foreground))" />
                      <YAxis yAxisId="r" orientation="right" tick={{ fontSize: 10 }} stroke="hsl(var(--muted-foreground))" />
                      <Tooltip contentStyle={tooltipStyle} />
                      <Legend wrapperStyle={{ fontSize: 11 }} />
                      <Line yAxisId="l" name={L("Prezzo €", "Price €")} type="monotone" dataKey="price" stroke="hsl(var(--primary))" strokeWidth={2} dot={{ r: 2 }} connectNulls />
                      <Line yAxisId="r" name={L("Camere", "Rooms")} type="monotone" dataKey="rooms" stroke="hsl(var(--chart-2))" strokeWidth={2} dot={{ r: 2 }} connectNulls />
                    </ComposedChart>
                  </ResponsiveContainer>
                ) : <EmptyChart label={L("Pickup in accumulo: servono più rilevazioni per questa data.", "Pickup accumulating: more snapshots needed for this date.")} />}
              </Card>
            </div>

            {/* 6-month trend */}
            <Card testid="cd-monthly-chart">
              <h3 className="text-sm uppercase tracking-widest font-bold text-muted-foreground mb-4">{L("Trend storico (ultimi 6 mesi)", "Historical trend (last 6 months)")}</h3>
              {monthly.length >= 1 ? (
                <ResponsiveContainer width="100%" height={220}>
                  <ComposedChart data={monthly}>
                    <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                    <XAxis dataKey="month" tick={{ fontSize: 10 }} stroke="hsl(var(--muted-foreground))" />
                    <YAxis yAxisId="l" tick={{ fontSize: 10 }} stroke="hsl(var(--muted-foreground))" />
                    <YAxis yAxisId="r" orientation="right" domain={[0, 100]} tick={{ fontSize: 10 }} stroke="hsl(var(--muted-foreground))" />
                    <Tooltip contentStyle={tooltipStyle} />
                    <Legend wrapperStyle={{ fontSize: 11 }} />
                    <Bar yAxisId="l" name={L("Prezzo medio €", "Avg price €")} dataKey="avgPrice" fill="hsl(var(--primary))" fillOpacity={0.7} radius={[4, 4, 0, 0]} />
                    <Line yAxisId="r" name={L("Occ. media %", "Avg occ. %")} type="monotone" dataKey="avgOcc" stroke="hsl(var(--chart-4))" strokeWidth={2} dot={{ r: 3 }} connectNulls />
                  </ComposedChart>
                </ResponsiveContainer>
              ) : <EmptyChart label={L("Trend mensile in accumulo.", "Monthly trend accumulating.")} />}
            </Card>
          </div>
        )}
      </div>
    </div>
  );
}
